import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { SettingsResponse, Visit } from '@cloudsign/shared';
import type { MemberSearchItem, TodayBoard } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { errorText, t } from '../../lib/i18n';
import { Alert, Button, SelectField, TextField } from '../../components/ui';
import { FieldInput, checkAnswer } from '../FieldInput';
import type { HostValue, Raw } from '../FieldInput';
import { FieldList, MemberSearch } from './api';
import { Overlay } from './parts';
import { useCan, useCaps } from '../../lib/session';

type Answers = Record<string, Raw>;

function PickList({ members, onPick, label }: { members: MemberSearchItem[]; onPick: (m: MemberSearchItem) => void; label: string }) {
  if (members.length === 0) return null;
  return (
    <ul aria-label={label} className="max-h-40 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
      {members.map((m) => (
        <li key={m.id}>
          <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onPick(m)}>
            {m.fullName}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function SignInDialog({
  siteId,
  groups,
  onClose,
  onDone,
}: {
  siteId: string;
  groups: TodayBoard['groups'];
  onClose: () => void;
  onDone: () => void;
}) {
  const [groupId, setGroupId] = useState(groups[0]?.groupId ?? '');
  const group = groups.find((g) => g.groupId === groupId);
  const repeat = group?.kind === 'repeat';
  const [memberTerm, setMemberTerm] = useState('');
  const [member, setMember] = useState<MemberSearchItem | null>(null);
  const [visitor, setVisitor] = useState('');
  const [hostTerm, setHostTerm] = useState('');
  const [host, setHost] = useState<MemberSearchItem | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const fieldsQ = useQuery({
    queryKey: ['today', 'signin-fields', siteId],
    queryFn: () => api.get(`/fields/sites/${siteId}`, { query: { scope: 'sign_in' }, schema: FieldList }),
  });
  // Groups that require ID checks: the portal has no camera, so remind the person at the desk to look at the ID (id-checks.md section 4).
  const caps = useCaps();
  const idChecksOn = useCan('groups.view') && caps.modules.some((m) => m.key === 'id-checks' && m.enabled);
  const idModeQ = useQuery({
    queryKey: ['today', 'id-check-mode', groupId],
    enabled: idChecksOn && groupId !== '',
    retry: false,
    queryFn: () => api.get(`/groups/${groupId}/settings/id-checks`, { schema: SettingsResponse }),
  });
  const idRequired = idModeQ.data?.values['mode'] === 'required';
  const fields = (fieldsQ.data ?? []).filter((f) => f.status === 'active' && f.groupIds.includes(groupId));
  const hostField = fields.some((f) => f.typeKey === 'host');

  const memberQ = useQuery({
    queryKey: ['today', 'member-search', groupId, memberTerm],
    queryFn: () => api.get('/visits/member-search', { query: { siteId, groupId, search: memberTerm.trim(), pageSize: 8 }, schema: MemberSearch }),
    enabled: Boolean(repeat && memberTerm.trim().length > 0 && !member),
  });
  const repeatGroups = groups.filter((g) => g.kind === 'repeat');
  const hostQs = useQueries({
    queries: repeatGroups.map((g) => ({
      queryKey: ['today', 'host-search', g.groupId, hostTerm],
      queryFn: () => api.get('/visits/member-search', { query: { siteId, groupId: g.groupId, search: hostTerm.trim(), pageSize: 5 }, schema: MemberSearch }),
      enabled: hostTerm.trim().length > 1 && !host,
    })),
  });
  const hostResults = hostQs.flatMap((q) => q.data?.items ?? []);

  const signIn = useMutation({
    mutationFn: (body: unknown) => api.post('/visits', { body, schema: Visit }),
    onSuccess: onDone,
    onError: (e) => {
      setServerError(e instanceof ApiClientError ? errorText(e.body) : t('error.internal'));
      if (e instanceof ApiClientError && e.fields) setProblems(e.fields);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const gaps: string[] = [];
    if (repeat ? !member : !visitor.trim()) gaps.push('who');
    const found: Record<string, string> = {};
    const list: { fieldId: string; value: unknown }[] = [];
    let hostFromField: HostValue | undefined;
    for (const f of fields) {
      const r = checkAnswer(f, answers[f.id]);
      if (!r.ok) found[f.id] = r.messageKey;
      else if (r.value !== null) {
        list.push({ fieldId: f.id, value: r.value });
        if (f.typeKey === 'host') hostFromField = r.value as HostValue;
      }
    }
    setMissing(gaps);
    setProblems(found);
    if (gaps.length || Object.keys(found).length) return;
    const hostMemberId = host?.id ?? hostFromField?.memberId;
    signIn.mutate({
      siteId,
      groupId,
      ...(repeat && member ? { memberId: member.id } : { visitorName: visitor.trim() }),
      ...(hostMemberId ? { hostMemberId } : {}),
      answers: list,
    });
  };

  return (
    <Overlay
      title={t('today.signIn.title')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" type="submit" form="today-signin-form" disabled={signIn.isPending}>
            {t('today.signIn.submit')}
          </Button>
        </>
      }
    >
      <form id="today-signin-form" onSubmit={submit} className="space-y-4" noValidate>
        {serverError ? <Alert>{serverError}</Alert> : null}
        {idRequired ? <Alert kind="info">{t('idResult.byEye')}</Alert> : null}
        <SelectField
          label={t('today.signIn.group')}
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            setMember(null);
            setMemberTerm('');
            setAnswers({});
            setProblems({});
          }}
        >
          {groups.map((g) => (
            <option key={g.groupId} value={g.groupId}>
              {g.name}
            </option>
          ))}
        </SelectField>

        {repeat ? (
          <div className="space-y-2">
            {member ? (
              <p className="flex items-center justify-between rounded-md bg-brand-soft px-3 py-2 text-sm">
                <span>{member.fullName}</span>
                <button type="button" className="text-brand underline" onClick={() => setMember(null)}>
                  {t('today.signIn.change')}
                </button>
              </p>
            ) : (
              <>
                <TextField
                  label={t('today.signIn.member')}
                  placeholder={t('today.signIn.memberSearch')}
                  value={memberTerm}
                  error={missing.includes('who') ? t('today.signIn.whoRequired') : undefined}
                  onChange={(e) => setMemberTerm(e.target.value)}
                />
                <PickList members={memberQ.data?.items ?? []} label={t('today.signIn.memberResults')} onPick={(m) => setMember(m)} />
              </>
            )}
          </div>
        ) : (
          <TextField
            label={t('today.signIn.visitorName')}
            value={visitor}
            maxLength={120}
            error={missing.includes('who') ? t('today.signIn.whoRequired') : undefined}
            onChange={(e) => setVisitor(e.target.value)}
          />
        )}

        {repeatGroups.length > 0 && !hostField ? (
          <div className="space-y-2">
            {host ? (
              <p className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                <span>{t('today.signIn.hostChosen', { name: host.fullName })}</span>
                <button type="button" className="text-brand underline" onClick={() => setHost(null)}>
                  {t('today.signIn.change')}
                </button>
              </p>
            ) : (
              <>
                <TextField label={t('today.signIn.host')} hint={t('today.signIn.hostHint')} value={hostTerm} onChange={(e) => setHostTerm(e.target.value)} />
                <PickList members={hostResults} label={t('today.signIn.hostResults')} onPick={(m) => setHost(m)} />
              </>
            )}
          </div>
        ) : null}

        {fields.map((f) => (
          <FieldInput
            key={f.id}
            field={f}
            raw={answers[f.id]}
            error={problems[f.id]}
            repeatGroupIds={repeatGroups.map((g) => g.groupId)}
            siteId={siteId}
            onChange={(v) => setAnswers((a) => ({ ...a, [f.id]: v }))}
          />
        ))}
      </form>
    </Overlay>
  );
}
